import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  retrieveApiData,
  deleteApiData,
  resetApiData,
  postApiData,
} from "../services/connect";

interface ResetApiDataPayload {
  datasets?: string[];
}

interface ApiResponseData {
  data: unknown;
}

interface PostRejectedPayload {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
    };
  };
}

interface ConnectState {
  status: string;
  showLoadingIndicator: boolean;
  datasets: Record<string, unknown>;
  errorMessage: string;
}

const initialState: ConnectState = {
  status: "idle",
  showLoadingIndicator: false,
  datasets: {},
  errorMessage: "",
};

const connectSlice = createSlice({
  name: "connect",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        resetApiData,
        (state, action: PayloadAction<ResetApiDataPayload | undefined>) => {
          const { payload } = action;
          state.status = "idle";
          state.errorMessage = "";
          // Clear up selected datasets
          if (Array.isArray(payload?.datasets)) {
            for (const name of payload.datasets) {
              delete state.datasets[name];
            }
          } else {
            state.datasets = {};
          }
        },
      )
      .addCase(retrieveApiData.pending, (state) => {
        state.status = !state.status.startsWith("delete")
          ? "get:pending"
          : "get:followup:pending";
        state.showLoadingIndicator = true;
      })
      .addCase(retrieveApiData.fulfilled, (state, action) => {
        state.status = "get:success";
        state.showLoadingIndicator = false;
        const apiName = action.meta.arg.queryParams?.["apiName"] as string;
        const response = action.payload as ApiResponseData;
        state.datasets[apiName] = response.data;
      })
      .addCase(retrieveApiData.rejected, (state, action) => {
        state.status = "get:rejected";
        state.showLoadingIndicator = true;
        state.errorMessage = action.error.message ?? "";
      })
      .addCase(deleteApiData.pending, (state) => {
        state.status = "delete:pending";
        state.showLoadingIndicator = true;
      })
      .addCase(deleteApiData.fulfilled, (state, action) => {
        state.status = "delete:success";
        state.showLoadingIndicator = true;
        const apiName = (action.meta.arg as unknown as Record<string, unknown>)[
          "apiName"
        ] as string;
        const response = action.payload as ApiResponseData;
        state.datasets[apiName] = response.data;
      })
      .addCase(deleteApiData.rejected, (state, action) => {
        state.status = "delete:rejected";
        state.showLoadingIndicator = true;
        state.errorMessage = action.error.message ?? "";
      })
      .addCase(postApiData.pending, (state) => {
        state.status = "post:pending";
        state.showLoadingIndicator = true;
      })
      .addCase(postApiData.fulfilled, (state) => {
        state.status = "post:success";
        state.showLoadingIndicator = false;
      })
      .addCase(postApiData.rejected, (state, action) => {
        state.status = "post:rejected";
        state.showLoadingIndicator = true;
        const errorPayload = action.payload as PostRejectedPayload | undefined;
        const customMessage = errorPayload?.response?.data?.error?.message;
        state.errorMessage = customMessage ?? action.error.message ?? "";
      });
  },
});

export default connectSlice.reducer;
